import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChatMessageItem, DisplayableSearchResult, ChatMessage as ChatMessageType } from './types';
import {
    BOT_GREETING_MESSAGE,
    PRIMARY_COLOR,
    MAX_MESSAGES_TO_STORE,
} from './constants';
import { sendChatMessage } from './services/apiService';
import { saveMessagesToLocalStorage, loadMessagesFromLocalStorage, countWords, generateSessionId } from './utils';
import { ChatInput } from './components/ChatInput';
import { ChatMessage } from './components/ChatMessage';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useSpeechSynthesis } from './hooks/useSpeechSynthesis';
import { useLiveAudio } from './hooks/useLiveAudio';

const GENERIC_BOT_ERROR_MESSAGE = "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuch es noch einmal.";

interface AppProps {
    isVisible: boolean;
}

const App: React.FC<AppProps> = ({ isVisible }) => {
    const [messages, setMessages] = useState<ChatMessageItem[]>(loadMessagesFromLocalStorage);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sessionId, setSessionId] = useState<string>('');
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatMessagesContainerRef = useRef<HTMLDivElement>(null);
    const { speak, cancel: cancelSpeech } = useSpeechSynthesis();

    // Track current message IDs during live audio (for accumulating transcript fragments)
    const currentUserMsgIdRef = useRef<string | null>(null);
    const currentModelMsgIdRef = useRef<string | null>(null);

    const scrollToBottom = useCallback(() => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }, []);

    // Live audio callbacks
    const liveAudioCallbacks = useMemo(() => ({
        onTranscript: (text: string, role: 'user' | 'model') => {
            if (role === 'user') {
                // Finalize previous model message
                currentModelMsgIdRef.current = null;

                // Accumulate user transcript fragments into one message
                if (!currentUserMsgIdRef.current) {
                    const msgId = `live-user-${Date.now()}`;
                    currentUserMsgIdRef.current = msgId;
                    setMessages(prev => [...prev, {
                        id: msgId,
                        content: text,
                        sender: 'user' as const,
                        timestamp: new Date(),
                    }]);
                } else {
                    const msgId = currentUserMsgIdRef.current;
                    setMessages(prev => prev.map(m =>
                        m.id === msgId
                            ? { ...m, content: (typeof m.content === 'string' ? m.content : '') + ' ' + text }
                            : m
                    ));
                }
                scrollToBottom();
            } else {
                // Finalize previous user message
                currentUserMsgIdRef.current = null;

                // Accumulate model transcript fragments into one message
                if (!currentModelMsgIdRef.current) {
                    const msgId = `live-bot-${Date.now()}`;
                    currentModelMsgIdRef.current = msgId;
                    setMessages(prev => [...prev, {
                        id: msgId,
                        content: text,
                        sender: 'bot' as const,
                        timestamp: new Date(),
                    }]);
                } else {
                    const msgId = currentModelMsgIdRef.current;
                    setMessages(prev => prev.map(m =>
                        m.id === msgId
                            ? { ...m, content: (typeof m.content === 'string' ? m.content : '') + ' ' + text }
                            : m
                    ));
                }
                scrollToBottom();
            }
        },
        onSearchResults: (results: { products: unknown[]; webpages: unknown[] }) => {
            // Attach search results to the current model message
            const msgId = currentModelMsgIdRef.current;
            if (msgId) {
                setMessages(prev => prev.map(m =>
                    m.id === msgId
                        ? {
                            ...m,
                            unifiedSearchResults: {
                                products: results.products as DisplayableSearchResult[],
                                webpages: results.webpages as DisplayableSearchResult[],
                            },
                            searchResultLayout: (results.webpages.length > results.products.length ? 'info-first' : 'product-first') as 'info-first' | 'product-first',
                            areProductsVisible: true,
                        }
                        : m
                ));
            }
        },
        onError: (message: string) => {
            console.error('[LiveAudio] Error:', message);
            setMessages(prev => [...prev, {
                id: `error-${Date.now()}`,
                content: message,
                sender: 'bot' as const,
                timestamp: new Date(),
            }]);
        },
        onSessionEnd: () => {
            currentUserMsgIdRef.current = null;
            currentModelMsgIdRef.current = null;
        },
    }), [scrollToBottom]);

    const { isLive, isConnecting, isAwaitingPermission, startSession: startLiveSession, stopSession: stopLiveSession, sendDialect, getUserLevel, getModelLevel } = useLiveAudio(liveAudioCallbacks);
    const [isSwissGerman, setIsSwissGerman] = useState(false);

    // Initialize session on mount
    useEffect(() => {
        if (!sessionId) {
            setSessionId(generateSessionId());
        }
    }, [sessionId]);

    // Save messages to local storage
    useEffect(() => {
        saveMessagesToLocalStorage(messages);
    }, [messages]);

    // Initialize chat with greeting
    useEffect(() => {
        if (isVisible && messages.length === 0 && sessionId) {
            setMessages([{
                id: 'initial-greeting',
                content: BOT_GREETING_MESSAGE,
                sender: 'bot',
                timestamp: new Date(),
            }]);
        }
    }, [isVisible, sessionId]);

    const handleProductCardHover = useCallback((productId: string | null) => {
        // Hover state logic can be expanded here
    }, []);

    const handleToggleProductsVisibility = (messageId: string) => {
        setMessages(prev => prev.map(msg => 
            msg.id === messageId 
            ? { ...msg, areProductsVisible: !msg.areProductsVisible } 
            : msg
        ));
    };

    // Listen for Swiss German toggle from parent (GTM snippet)
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.data?.type === 'toggle-swiss-german') {
                const newValue = event.data.value;
                setIsSwissGerman(newValue);
                sendDialect(newValue);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [sendDialect]);

    const handleClearChat = useCallback(() => {
        cancelSpeech();
        if (isLive) stopLiveSession();

        // Reset Messages to Greeting
        setMessages([{
            id: 'initial-greeting',
            content: BOT_GREETING_MESSAGE,
            sender: 'bot',
            timestamp: new Date(),
        }]);

        // New Session
        setSessionId(generateSessionId());
    }, [cancelSpeech, isLive, stopLiveSession]);

    const handleSendMessage = useCallback(async (inputText: string, fromVoice?: boolean) => {
        if (!inputText.trim() || isLoading || !sessionId) return;

        // Cancel any ongoing speech
        cancelSpeech();

        // Build history from existing text messages (skip loading spinners / JSX nodes)
        const history: ChatMessageType[] = messages
            .filter(m => typeof m.content === 'string' && (m.sender === 'user' || m.sender === 'bot'))
            .slice(-MAX_MESSAGES_TO_STORE)
            .map(m => ({
                id: m.id,
                role: (m.sender === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
                content: m.content as string,
                timestamp: m.timestamp.toISOString()
            }));

        const userMessage: ChatMessageItem = {
            id: Date.now().toString(),
            content: inputText,
            sender: 'user',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);

        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);

        setIsLoading(true);

        const botMessageId = `bot-${Date.now()}`;
        setMessages(prev => [...prev, {
            id: botMessageId,
            content: <LoadingSpinner />,
            sender: 'bot',
            timestamp: new Date(),
            isStreaming: true
        }]);

        try {
            const response = await sendChatMessage(inputText, sessionId, history, isSwissGerman);

            if (response.success && response.data) {
                const { message, searchResults } = response.data;

                // Determine layout based on content
                let searchResultLayout: 'info-first' | 'product-first' = 'product-first';
                if (searchResults) {
                    const products = searchResults.products || [];
                    const webpages = searchResults.webpages || [];
                    searchResultLayout = webpages.length > products.length ? 'info-first' : 'product-first';
                }

                setMessages(prev => prev.map(msg => msg.id === botMessageId ? {
                    ...msg,
                    content: message,
                    isStreaming: false,
                    unifiedSearchResults: searchResults ? {
                        products: searchResults.products || [],
                        webpages: searchResults.webpages || []
                    } : undefined,
                    searchResultLayout,
                    areProductsVisible: true
                } : msg));

                // Speak the response if the message was voice-triggered
                if (fromVoice && message) {
                    speak(message);
                }
            } else {
                throw new Error(response.error || 'Unknown error');
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setMessages(prev => prev.map(m => m.id === botMessageId ? { 
                ...m, 
                content: GENERIC_BOT_ERROR_MESSAGE, 
                isStreaming: false 
            } : m));
        }

        setIsLoading(false);
        
        // Scroll to bottom after response
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }, [sessionId, isLoading, cancelSpeech, speak, isSwissGerman]);

    return (
        <div className="flex flex-col h-full w-full bg-white shadow-none rounded-none overflow-hidden">
            <div
                ref={chatMessagesContainerRef}
                className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-white chat-messages-scrollbar"
            >
                {messages.map((msg) => (
                    <ChatMessage 
                        key={msg.id} 
                        message={msg}
                        onCardHover={handleProductCardHover} 
                        onToggleProductsVisibility={handleToggleProductsVisibility}
                    /> 
                ))}
                <div ref={messagesEndRef} />
            </div>
            <ChatInput
                onSendMessage={handleSendMessage}
                onClearChat={handleClearChat}
                isLoading={isLoading}
                isLive={isLive}
                isConnecting={isConnecting}
                isAwaitingPermission={isAwaitingPermission}
                onStartLiveSession={() => startLiveSession(sessionId, { swissGerman: isSwissGerman })}
                onStopLiveSession={stopLiveSession}
                getUserLevel={getUserLevel}
                getModelLevel={getModelLevel}
            />
        </div>
    );
};

export default App;
